import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/DataTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useFlaggedMessages, FlaggedMessage } from '@/hooks/useFlaggedMessages';
import { MessageSquare, Download, Search, Filter, Eye, Users, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function AdminSecurityPage() {
  const { flaggedMessages, stats, loading, error, fetchFlaggedMessages, exportToCSV } = useFlaggedMessages();
  const [searchTerm, setSearchTerm] = useState('');
  const [detectionTypeFilter, setDetectionTypeFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleFilter = () => {
    fetchFlaggedMessages({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      detectionType: (detectionTypeFilter && detectionTypeFilter !== 'all') ? detectionTypeFilter : undefined
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDetectionTypeFilter('all');
    setStartDate('');
    setEndDate('');
    fetchFlaggedMessages();
  };

  const filteredMessages = flaggedMessages.filter(msg => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      msg.user_email?.toLowerCase().includes(searchLower) ||
      msg.organization_name?.toLowerCase().includes(searchLower) ||
      msg.job_title?.toLowerCase().includes(searchLower) ||
      msg.flagged_content.toLowerCase().includes(searchLower) ||
      msg.detection_type.toLowerCase().includes(searchLower)
    );
  });

  const getDetectionTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'phone_number': return 'secondary';
      case 'email': return 'secondary';
      case 'social_media': return 'outline';
      case 'url': return 'outline';
      default: return 'default';
    }
  };

  const getDetectionTypeLabel = (type: string) => {
    switch (type) {
      case 'phone_number': return 'Phone Number';
      case 'email': return 'Email Address';
      case 'social_media': return 'Social Media';
      case 'url': return 'Website URL';
      default: return type.replace('_', ' ').toUpperCase();
    }
  };

  const columns = [
    {
      key: 'created_at' as keyof FlaggedMessage,
      label: 'Date',
      render: (value: any, msg: FlaggedMessage) => (
        <div className="text-sm">
          {new Date(msg.created_at).toLocaleDateString()}
          <div className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(msg.created_at))} ago
          </div>
        </div>
      )
    },
    {
      key: 'user_email' as keyof FlaggedMessage,
      label: 'User',
      render: (value: any, msg: FlaggedMessage) => (
        <div className="text-sm">
          <div className="font-medium">{msg.user_email}</div>
          <div className="text-xs text-muted-foreground">
            {msg.user_role}
          </div>
        </div>
      )
    },
    {
      key: 'organization_name' as keyof FlaggedMessage,
      label: 'Organization',
      render: (value: any, msg: FlaggedMessage) => (
        <div className="text-sm font-medium">
          {msg.organization_name}
        </div>
      )
    },
    {
      key: 'job_title' as keyof FlaggedMessage,
      label: 'Job',
      render: (value: any, msg: FlaggedMessage) => (
        <div className="text-sm">
          <div className="font-medium">{msg.job_title}</div>
          <div className="text-xs text-muted-foreground">
            ID: {msg.job_public_id}
          </div>
        </div>
      )
    },
    {
      key: 'detection_type' as keyof FlaggedMessage,
      label: 'Type',
      render: (value: any, msg: FlaggedMessage) => (
        <Badge variant={getDetectionTypeBadgeVariant(msg.detection_type)}>
          {getDetectionTypeLabel(msg.detection_type)}
        </Badge>
      )
    },
    {
      key: 'flagged_content' as keyof FlaggedMessage,
      label: 'Shared Content',
      render: (value: any, msg: FlaggedMessage) => (
        <div className="text-sm font-mono bg-muted px-2 py-1 rounded max-w-32 truncate">
          {msg.flagged_content}
        </div>
      )
    },
    {
      key: 'message_sent' as keyof FlaggedMessage,
      label: 'Sent',
      render: (value: any, msg: FlaggedMessage) => (
        <div className="flex items-center gap-1">
          {msg.message_sent ? (
            <>
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-600">Yes</span>
            </>
          ) : (
            <>
              <XCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">No</span>
            </>
          )}
        </div>
      )
    },
    {
      key: 'message_content' as keyof FlaggedMessage,
      label: 'Message Preview',
      render: (value: any, msg: FlaggedMessage) => (
        <div className="text-sm text-muted-foreground max-w-64 truncate">
          {msg.message_content.substring(0, 100)}
          {msg.message_content.length > 100 && '...'}
        </div>
      )
    }
  ];

  if (loading && flaggedMessages.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <h3 className="font-semibold text-destructive">Error Loading Data</h3>
          <p className="text-sm text-destructive/80 mt-1">{error}</p>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outline" 
            size="sm" 
            className="mt-2"
          >
            Reload Page
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <MessageSquare className="h-8 w-8" />
            Communication Activity Log
          </h1>
          <p className="text-muted-foreground">
            Monitor flagged messages where contact information was shared
          </p>
        </div>
        <Button onClick={exportToCSV} variant="outline" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Flagged</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalFlagged || 0}</div>
            <p className="text-xs text-muted-foreground">All time instances</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Phone Numbers</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.byDetectionType.phone_number || 0}
            </div>
            <p className="text-xs text-muted-foreground">Phone numbers shared</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Email Addresses</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.byDetectionType.email || 0}
            </div>
            <p className="text-xs text-muted-foreground">Emails shared</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Frequent Sharers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.frequentSharers.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">Unique users</p>
          </CardContent>
        </Card>
      </div>

      {/* Detection Type Breakdown */}
      {stats && Object.keys(stats.byDetectionType).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Detection Type Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.byDetectionType).map(([type, count]) => (
                <div key={type} className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-sm text-muted-foreground">
                    {getDetectionTypeLabel(type)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            
            <Select value={detectionTypeFilter} onValueChange={setDetectionTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Detection type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="phone_number">Phone Number</SelectItem>
                <SelectItem value="email">Email Address</SelectItem>
                <SelectItem value="social_media">Social Media</SelectItem>
                <SelectItem value="url">Website URL</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="Start date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <Input
              type="date"
              placeholder="End date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />

            <div className="flex gap-2">
              <Button onClick={handleFilter} variant="outline">
                Apply
              </Button>
              <Button onClick={clearFilters} variant="ghost">
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Flagged Messages Table */}
      <Card>
        <CardHeader>
          <CardTitle>Flagged Messages ({filteredMessages.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={filteredMessages}
            columns={columns}
          />
        </CardContent>
      </Card>

      {/* Frequent Sharers */}
      {stats && stats.frequentSharers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Frequent Sharers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.frequentSharers.slice(0, 10).map((sharer, index) => (
                <div key={sharer.user_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">#{index + 1}</Badge>
                    <div>
                      <div className="font-medium">{sharer.user_email}</div>
                      <div className="text-sm text-muted-foreground">
                        {sharer.share_count} instances
                      </div>
                    </div>
                  </div>
                  <Badge variant="secondary">
                    {sharer.share_count}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
